import { z } from "zod";
import { Action, Person, Problem } from "./wire.js";

/**
 * Dashboard view shapes: what cloud/api returns to apps/web (and, where they overlap, the phones).
 * cloud/api may return a superset (extra fields); clients parse with these schemas.
 */

export const MeView = z.object({
  person: Person,
  account: z.object({ id: z.string(), name: z.string().max(100) }),
  hasPasskey: z.boolean(),
});
export type MeView = z.infer<typeof MeView>;

export const FeedItem = z.object({
  id: z.string(),
  kind: z.enum(["fixed", "told", "needed"]),
  title: z.string().max(200),
  detail: z.string().max(500),
  at: z.number().int(),
  problemId: z.string().nullable(),
});
export type FeedItem = z.infer<typeof FeedItem>;

/**
 * The outcome counts every client shows, with one meaning everywhere (decided 22 Oct 2026, bug #18):
 * - `resolved`: problems resolved in the window, whoever fixed them (the total).
 * - `fixedByHelper`: resolved with no human ack or approval.
 * - `neededYou`: a person acked or approved (resolved or not).
 * Problems are counted by when they opened, like WeekStats.
 */
export const OutcomeCounts = z.object({
  resolved: z.number().int().nonnegative(),
  fixedByHelper: z.number().int().nonnegative(),
  neededYou: z.number().int().nonnegative(),
});
export type OutcomeCounts = z.infer<typeof OutcomeCounts>;

export const HomeView = z.object({
  stats: z.object({
    /** Same as `fixedByHelper`. Kept for older clients. */
    fixed: z.number().int().nonnegative(),
    told: z.number().int().nonnegative(),
    /** Same as `neededYou`. Kept for older clients. */
    needed: z.number().int().nonnegative(),
    /** Additive (see OutcomeCounts). Optional so replies from older servers still parse. */
    resolved: z.number().int().nonnegative().optional(),
    fixedByHelper: z.number().int().nonnegative().optional(),
    neededYou: z.number().int().nonnegative().optional(),
  }),
  /** Fixed by the helper alone today. Same as `today.fixedByHelper`. */
  fixedToday: z.number().int().nonnegative(),
  /** Additive: today's counts (since UTC midnight), same meanings as `stats`. */
  today: OutcomeCounts.optional(),
  /** Days since the last outage across all apps; null when there is no history yet. */
  upDays: z.number().int().nonnegative().nullable(),
  feed: z.array(FeedItem),
});
export type HomeView = z.infer<typeof HomeView>;

export const WeeklyView = z.object({
  /** Same as `fixedByHelper`. Kept for older clients. */
  fixed: z.number().int().nonnegative(),
  /** Same as `neededYou`. Kept for older clients. */
  needed: z.number().int().nonnegative(),
  /** Additive (see OutcomeCounts). Optional so replies from older servers still parse. */
  resolved: z.number().int().nonnegative().optional(),
  fixedByHelper: z.number().int().nonnegative().optional(),
  neededYou: z.number().int().nonnegative().optional(),
  /** Total seconds a human spent on alerts this week. */
  secondsSpent: z.number().int().nonnegative(),
});
export type WeeklyView = z.infer<typeof WeeklyView>;

export const ProjectState = z.enum(["waiting", "up", "down", "paused"]);
export type ProjectState = z.infer<typeof ProjectState>;

export const ProjectView = z.object({
  id: z.string(),
  name: z.string().max(100),
  /** Host id from the connectors registry ("vercel", "railway", ...), null when unknown. */
  host: z.string().max(40).nullable(),
  state: ProjectState,
  since: z.number().int().nullable(),
});
export type ProjectView = z.infer<typeof ProjectView>;

export const ProjectStatusView = z.object({
  online: z.boolean(),
  /** Service name from the first event, e.g. "mayas-shop". */
  service: z.string().max(100).nullable(),
  /** Plain-words list of what we can see: "checkout", "payments (Stripe)". */
  found: z.array(z.string().max(100)),
  /** Days since the app's last outage (or its first heartbeat); null before any heartbeat. Additive. */
  upDays: z.number().int().nonnegative().nullable().optional(),
});
export type ProjectStatusView = z.infer<typeof ProjectStatusView>;

export const TimelineItem = z.object({
  at: z.number().int(),
  text: z.string().max(300),
  hot: z.boolean().default(false),
});
export type TimelineItem = z.infer<typeof TimelineItem>;

export const ProblemView = z.object({
  problem: Problem,
  actions: z.array(Action),
  timeline: z.array(TimelineItem),
  /** Display name of the app this problem belongs to. */
  appName: z.string().max(100),
  /** When a person was first paged for this problem; null when nobody was paged. */
  pagedAt: z.number().int().nullable().default(null),
  /**
   * A person has to act now (see problemNeedsYou). Additive: older servers omit it (false). The
   * screen must not say "the helper is watching" while this is true.
   */
  needsYou: z.boolean().default(false),
});
export type ProblemView = z.infer<typeof ProblemView>;

/**
 * True when the problem is open and waiting on a person: a fix waits for a yes, the helper's fix
 * failed (it gave up), or someone was woken for it. False once someone is on it (acked), while a fix
 * runs (fixing) and when resolved. Pure: the API sends it, clients may compute it the same way.
 */
export function problemNeedsYou(p: {
  problem: Pick<Problem, "state" | "decision">;
  actions: ReadonlyArray<Pick<Action, "state">>;
  pagedAt: number | null;
}): boolean {
  if (p.problem.state !== "open") return false;
  if (p.actions.some((a) => a.state === "awaiting_approval" || a.state === "failed")) return true;
  return p.problem.decision.level === "wake" && p.pagedAt !== null;
}

export const AlertSettingsView = z.object({
  wakeFor: z.array(z.string().max(100)),
  /** `tz` is additive: the IANA zone the times are in. */
  quietHours: z.object({ from: z.string(), to: z.string(), tz: z.string().optional() }).nullable(),
  summary: z.object({ at: z.string(), where: z.string() }).nullable(),
});
export type AlertSettingsView = z.infer<typeof AlertSettingsView>;

export const PlanView = z.object({
  plan: z.enum(["free", "team"]),
  /** People on the account and the plan's cap (Free: you + one backup = 2, D-12); null = no cap. */
  people: z.object({ used: z.number().int().nonnegative(), limit: z.number().int().nullable() }),
  apps: z.number().int().nonnegative(),
  limits: z.object({ people: z.number().int().nullable(), apps: z.number().int().nullable() }),
});
export type PlanView = z.infer<typeof PlanView>;

export const ChannelTestView = z.object({ ok: z.boolean(), at: z.number().int(), error: z.string().optional() });
export type ChannelTestView = z.infer<typeof ChannelTestView>;
